import React, { useEffect, useState } from "react";
import { Loader2, Sparkles, Trash2, Clapperboard } from "lucide-react";
import { api } from "@/lib/api";
import { SiteReader, useCopy } from "@/components/AmpliboeEmail";

const auth = () => { const t = localStorage.getItem("fliboe_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };
const PLAT_LABEL = { tiktok: "TikTok", instagram_reels: "Instagram Reels", youtube_shorts: "YouTube Shorts", youtube: "YouTube", facebook: "Facebook", x: "X", linkedin: "LinkedIn", pinterest: "Pinterest" };

function PromptCard({ item, idx, onUseVideo }) {
  const Copy = useCopy();
  const tags = (item.hashtags || []).map(h => (h.startsWith("#") ? h : `#${h}`)).join(" ");
  const Row = ({ k, label, text }) => text ? (
    <div className="mt-2">
      <div className="flex items-center justify-between"><span className="fl-mono text-[10px] uppercase tracking-widest text-white/50">{label}</span><Copy k={`${idx}-${k}`} text={text} /></div>
      <p className="text-sm text-white/85 mt-0.5 whitespace-pre-wrap">{text}</p>
    </div>
  ) : null;
  return (
    <div className="fl-card p-4 bg-white/[0.02] border-white/10" data-testid={`amp-prompt-item-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="fl-mono text-[11px] uppercase tracking-widest text-fuchsia-300">// {PLAT_LABEL[item.platform] || item.platform}</div>
        {onUseVideo && item.video_prompt && <button type="button" onClick={() => onUseVideo(item.video_prompt, item.platform)} className="fl-btn ghost !py-1 !px-2 text-[11px]" data-testid={`amp-prompt-use-${idx}`}><Clapperboard className="w-3 h-3" /> Make this video</button>}
      </div>
      <Row k="video" label="Video prompt" text={item.video_prompt} />
      <Row k="image" label="Image prompt" text={item.image_prompt} />
      <Row k="hook" label="Hook (first 2 seconds)" text={item.hook} />
      <Row k="caption" label="Caption" text={item.caption} />
      <Row k="tags" label="Hashtags" text={tags} />
    </div>
  );
}

export default function AmpliboePrompts({ onUseVideo }) {
  const [kinds, setKinds] = useState([]); const [platforms, setPlatforms] = useState([]);
  const [kind, setKind] = useState("brand"); const [name, setName] = useState(""); const [details, setDetails] = useState("");
  const [url, setUrl] = useState(""); const [site, setSite] = useState(null);
  const [picked, setPicked] = useState(["tiktok", "instagram_reels", "youtube_shorts"]);
  const [tone, setTone] = useState("cinematic, scroll-stopping");
  const [result, setResult] = useState(null); const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/ampliboe/email/templates").then(r => { setKinds(r.data.prompt_kinds || []); setPlatforms(r.data.prompt_platforms || []); });
    api.get("/ampliboe/prompts", { headers: auth() }).then(r => setHistory(r.data.prompts || [])).catch(() => {});
  }, []);

  const toggle = (p) => setPicked(ps => ps.includes(p) ? ps.filter(x => x !== p) : [...ps, p]);
  const run = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/ampliboe/prompts", { kind, name, details, url: url || null, platforms: picked, tone }, { headers: auth(), timeout: 90000 });
      setResult(data); setHistory(h => [data, ...h].slice(0, 30));
    } catch (e) { const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : "Prompt maker failed"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    await api.delete(`/ampliboe/prompts/${id}`, { headers: auth() });
    setHistory(h => h.filter(x => x.prompt_id !== id)); if (result?.prompt_id === id) setResult(null);
  };

  return (
    <section className="max-w-6xl mx-auto px-5 py-8 grid lg:grid-cols-5 gap-6" data-testid="amp-prompts-section">
      <div className="lg:col-span-2 space-y-3">
        <div className="fl-mono text-[11px] uppercase tracking-widest text-fuchsia-400">// Prompt maker</div>
        <h2 className="fl-display text-3xl font-black">Prompts for anything you promote.</h2>
        <p className="text-slate-400 text-sm">A brand, a service, a music video or a short film. Get ready-to-paste video and image prompts, hooks and captions for every platform from one small AI call.</p>
        <div className="flex flex-wrap gap-1.5" data-testid="amp-prompt-kinds">
          {kinds.map(k => <button key={k.id} type="button" onClick={() => setKind(k.id)} className={`px-2 py-1 rounded-full border text-[11px] capitalize ${kind === k.id ? "bg-fuchsia-500/20 border-fuchsia-400 text-white" : "border-white/15 text-white/70"}`} data-testid={`amp-prompt-kind-${k.id}`}>{k.id.replace("_", " ")}</button>)}
        </div>
        <input className="fl-input w-full" value={name} onChange={e => setName(e.target.value)} placeholder="Name (e.g. Neon Rain, Maya's Bakery)" data-testid="amp-prompt-name" />
        <textarea className="fl-input w-full min-h-[80px]" value={details} onChange={e => setDetails(e.target.value)} placeholder="A few details: story, audience, vibe, offer…" data-testid="amp-prompt-details" />
        <SiteReader url={url} setUrl={setUrl} site={site} setSite={setSite} testPrefix="amp-prompt" />
        <div>
          <span className="fl-mono text-[10px] uppercase tracking-widest text-white/60">Platforms</span>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="amp-prompt-platforms">
            {platforms.map(p => <button key={p} type="button" onClick={() => toggle(p)} className={`px-2 py-1 rounded border text-[11px] ${picked.includes(p) ? "bg-fuchsia-500/20 border-fuchsia-400 text-white" : "border-white/15 text-white/60"}`} data-testid={`amp-prompt-plat-${p}`}>{PLAT_LABEL[p]}</button>)}
          </div>
        </div>
        <input className="fl-input w-full" value={tone} onChange={e => setTone(e.target.value)} data-testid="amp-prompt-tone" />
        {err && <div className="text-red-300 text-sm" data-testid="amp-prompt-err">{err}</div>}
        <button type="button" onClick={run} disabled={busy || !picked.length} className="fl-btn !bg-fuchsia-500 !text-black w-full justify-center" data-testid="amp-prompt-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Writing prompts…" : "Make prompts"}
        </button>
        {history.length > 0 && (
          <div className="pt-4" data-testid="amp-prompt-history">
            <div className="fl-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// Recent</div>
            {history.slice(0, 8).map(h => (
              <div key={h.prompt_id} className="flex items-center gap-2 mb-1.5">
                <button type="button" onClick={() => setResult(h)} className="flex-1 text-left p-2 rounded bg-white/[0.02] border border-white/10 text-xs truncate" data-testid={`amp-prompt-hist-${h.prompt_id}`}>
                  <span className="text-fuchsia-300 capitalize">{h.kind.replace("_", " ")}</span> · {h.name || h.url || h.details}
                </button>
                <button type="button" onClick={() => del(h.prompt_id)} className="text-red-300/70 hover:text-red-300" data-testid={`amp-prompt-del-${h.prompt_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="lg:col-span-3 space-y-3" data-testid="amp-prompt-results">
        {!result ? (
          <div className="fl-card p-8 bg-white/[0.02] border-white/10 text-center text-slate-400 text-sm min-h-[300px] grid place-items-center">Your platform-ready prompts will appear here.</div>
        ) : (result.items || []).map((it, i) => <PromptCard key={i} idx={i} item={it} onUseVideo={onUseVideo} />)}
      </div>
    </section>
  );
}
